import type {
  AdminUsageSeatRow,
  BalanceTransactionEntry,
  UsageRequestLogEntry,
  V1BillingStatement,
} from '@nadoutong/contracts';
import { getStatements, getTransactions } from '../api/billing.ts';
import { PlatformApiError } from '../api/client.ts';
import {
  type AdminUsageDashboardClientQuery,
  getAdminUsageDashboard,
  getUsageRequests,
  type UsageRequestsQuery,
} from '../api/usage.ts';
import { reportExportAuditEvent } from './audit.ts';
import { assembleStatementSheet, assembleTransactionSheet } from './billing.ts';
import { collectPagedRows } from './collect.ts';
import { exportFilename } from './filename.ts';
import { isValidExportRate } from './money.ts';
import { assembleRequestSheet } from './requests.ts';
import type { ExportResult } from './types.ts';
import { assembleUsageSheet } from './usage.ts';
import { downloadSheets } from './workbook.ts';

const REQUESTS_PAGE = 200;
const USAGE_PAGE = 100;
const STATEMENTS_PAGE = 120;
const TRANSACTIONS_PAGE = 100;

const UTC8_OFFSET_MS = 8 * 60 * 60 * 1000;

/** UTC+8 calendar date — same clock `Billing.tsx` uses for overdue. */
export function todayYmdUtc8(nowMs: number = Date.now()): string {
  return new Date(nowMs + UTC8_OFFSET_MS).toISOString().slice(0, 10);
}

export async function exportRequestLogs(options: {
  readonly fetchImpl: typeof fetch;
  readonly orgId: string;
  readonly query: Omit<UsageRequestsQuery, 'limit' | 'offset'>;
  readonly rangeDays: { readonly from: string; readonly to: string };
  readonly cnyPerUsd: number | null;
}): Promise<ExportResult> {
  if (!isValidExportRate(options.cnyPerUsd)) {
    return { status: 'error' };
  }
  void reportExportAuditEvent(options.fetchImpl, {
    type: 'export',
    surface: 'requests',
    range: { from: options.query.from ?? null, to: options.query.to ?? null },
  });
  try {
    const collected = await collectPagedRows<UsageRequestLogEntry>({
      pageSize: REQUESTS_PAGE,
      fetchPage: async (offset, limit) => {
        const page = await getUsageRequests(options.fetchImpl, {
          ...options.query,
          limit,
          offset,
          // Spreading the page query would inherit `detail: 'summary'`
          // and silently drop reasoning / image / unit-billing columns
          // on archived rows. Export always hydrates.
          detail: 'full',
        });
        return { rows: page.rows, total: page.total };
      },
    });
    if (collected.status === 'cap') return { status: 'cap' };
    await downloadSheets(
      [assembleRequestSheet(collected.rows, options.cnyPerUsd)],
      exportFilename('请求日志', options.rangeDays.from, options.rangeDays.to),
    );
    return { status: 'ok' };
  } catch {
    return { status: 'error' };
  }
}

export async function exportUsageSeats(options: {
  readonly fetchImpl: typeof fetch;
  readonly orgId: string;
  readonly query: AdminUsageDashboardClientQuery;
  readonly rangeDays: { readonly from: string; readonly to: string };
  readonly cnyPerUsd: number | null;
}): Promise<ExportResult> {
  if (!isValidExportRate(options.cnyPerUsd)) {
    return { status: 'error' };
  }
  void reportExportAuditEvent(options.fetchImpl, {
    type: 'export',
    surface: 'usage',
    range: { from: options.query.from ?? null, to: options.query.to ?? null },
  });
  try {
    const collected = await collectPagedRows<AdminUsageSeatRow>({
      pageSize: USAGE_PAGE,
      fetchPage: async (offset, limit) => {
        const page = await getAdminUsageDashboard(options.fetchImpl, {
          ...options.query,
          limit,
          offset,
        });
        return { rows: page.seats.rows, total: page.seats.total };
      },
    });
    if (collected.status === 'cap') return { status: 'cap' };
    await downloadSheets(
      [assembleUsageSheet(collected.rows, options.cnyPerUsd)],
      exportFilename('用量', options.rangeDays.from, options.rangeDays.to),
    );
    return { status: 'ok' };
  } catch {
    return { status: 'error' };
  }
}

export async function exportBilling(options: {
  readonly fetchImpl: typeof fetch;
  readonly orgId: string;
  readonly cnyPerUsd: number | null;
}): Promise<ExportResult> {
  if (!isValidExportRate(options.cnyPerUsd)) {
    return { status: 'error' };
  }
  void reportExportAuditEvent(options.fetchImpl, {
    type: 'export',
    surface: 'billing',
    range: { from: null, to: null },
  });
  try {
    const statements = await collectStatements(options.fetchImpl);
    if (statements.status === 'cap') return { status: 'cap' };
    const transactions = await collectTransactions(options.fetchImpl);
    if (transactions.status === 'cap') return { status: 'cap' };
    await downloadSheets(
      [
        assembleStatementSheet(statements.rows, options.cnyPerUsd, todayYmdUtc8()),
        assembleTransactionSheet(transactions.rows, options.cnyPerUsd),
      ],
      exportFilename('账单', null, null),
    );
    return { status: 'ok' };
  } catch {
    return { status: 'error' };
  }
}

async function collectStatements(
  fetchImpl: typeof fetch,
): Promise<
  | { readonly status: 'ok'; readonly rows: readonly V1BillingStatement[] }
  | { readonly status: 'cap' }
> {
  try {
    return await collectPagedRows<V1BillingStatement>({
      pageSize: STATEMENTS_PAGE,
      fetchPage: async (offset, limit) => {
        const page = await getStatements(fetchImpl, { limit, offset });
        return { rows: page.statements, total: null };
      },
    });
  } catch (error) {
    if (error instanceof PlatformApiError && error.status === 403) {
      return { status: 'ok', rows: [] };
    }
    throw error;
  }
}

async function collectTransactions(
  fetchImpl: typeof fetch,
): Promise<
  | { readonly status: 'ok'; readonly rows: readonly BalanceTransactionEntry[] }
  | { readonly status: 'cap' }
> {
  try {
    return await collectPagedRows<BalanceTransactionEntry>({
      pageSize: TRANSACTIONS_PAGE,
      fetchPage: async (offset, limit) => {
        const page = await getTransactions(fetchImpl, { limit, offset });
        return { rows: page.transactions, total: null };
      },
    });
  } catch (error) {
    if (error instanceof PlatformApiError && error.status === 403) {
      return { status: 'ok', rows: [] };
    }
    throw error;
  }
}
